import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { DndGridProps, DndIdentifiable } from './DndGrid'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { Context, memo, useContext, useEffect, useRef, useState } from 'react'

export interface DndItemProps<T extends DndIdentifiable> {
  context: Context<symbol | null>
  renderItem: (
    item: T,
    style: React.CSSProperties,
    ref: React.RefObject<HTMLElement | null>
  ) => React.ReactNode
  styles: DndGridProps<T>['styles']
  item: T
}

const Item = <T extends DndIdentifiable>({
  context,
  renderItem,
  styles,
  item,
}: DndItemProps<T>) => {
  type State = 'idle' | 'dragging' | 'over'

  const ref = useRef<HTMLElement | null>(null)
  const [state, setState] = useState<State>('idle')

  const instanceId = useContext(context)

  useEffect(() => {
    const el = ref.current
    if (!el) {
      return
    }

    return combine(
      draggable({
        element: el,
        getInitialData: () => ({ type: 'grid-item', id: item.id, instanceId }),
        onDragStart: () => setState('dragging'),
        onDrop: () => setState('idle'),
      }),
      dropTargetForElements({
        element: el,
        getData: () => ({ id: item.id }),
        getIsSticky: () => true,
        canDrop: ({ source }) =>
          source.data.instanceId === instanceId &&
          source.data.type === 'grid-item' &&
          source.data.src !== item.id,
        onDragEnter: () => setState('over'),
        onDragLeave: () => setState('idle'),
        onDrop: () => setState('idle'),
      })
    )
  }, [instanceId, item.id])

  // Fusion de la ref et des styles pour l'enfant
  return renderItem(item, (styles && styles[state]) ?? {}, ref)
}

export default memo(Item) as typeof Item
